import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useViewedContentStore} from '@/ts/store/viewed-content-store';
import {useUserStore} from '@/ts/store/user-store';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

/**
 * Reports a piece of content as watched, which is what moves the intro video out of the Today page
 * and into the FAQ once the backend has stamped it.
 *
 * The report is fire-and-forget on purpose: nothing on screen depends on it, and a failed call must
 * not interrupt someone who is watching a video. A failure only means the window closes later, on
 * the fourteenth day, or on the next viewing.
 *
 * Without an active contract there is nothing to report: the backend already keeps the video in the
 * FAQ for that user and refuses the call for want of a license, so sending it would only add a 403.
 */
export const useWatchedReport = (): {reportWatched(contentId: UuidDTO): void} => {
  const api = useApi();
  const {error} = useLogger();
  const store = useViewedContentStore();
  const userStore = useUserStore();

  const reportWatched = (contentId: UuidDTO): void => {
    if (!contentId || !userStore.userData?.activeContract || store.hasReportedWatched(contentId)) {
      return;
    }
    store.markReportedWatched(contentId);

    api.me.postBlockContentWatched(contentId).catch((e: unknown) => {
      store.forgetReportedWatched(contentId);
      error(`Reporting content ${contentId} as watched failed: ${String(e)}`)();
    });
  };

  return {reportWatched};
};
